import { unref, type Ref } from 'vue';
import { PageTypes } from '#shared/resolve-page';
import { getPreviousPage } from '~/composables/usePageHistoryStorage';
import { useSnippetsStore } from '~/stores/snippetsStore';

function isProductListingHistoryType(previousType: string | undefined, currentType: string | undefined): boolean {
    if (previousType === 'productListingPage' || previousType === PageTypes.ManufacturerCategory) {
        return true;
    }

    // Legacy entries: category PLPs were stored as Shopware `navigation` before the PLP overwrite ran.
    return previousType === PageTypes.Navigation && currentType === 'productDetailPage';
}

interface BreadcrumbInterface {
    name: string;
    url: string;
}

export function handleBreadCrumbPrefixClick(targetPath: string): void {
    const router = useRouter();
    const historyBack = window.history.state?.back;

    if (!targetPath) {
        if (historyBack) router.back();
        return;
    }

    if (!historyBack) {
        router.push(targetPath);
        return;
    }

    // if targetPath starts with /search then just use router.back();
    if (targetPath.includes('/search')) {
        router.back();
        return;
    }

    // if page history.back is identical to the url of the item we click, use back instead for a better user experience
    if (historyBack === targetPath) {
        router.back();
        return;
    }

    // sometimes query parameters are injected or changed without our knowledge
    if (historyBack.split('?')[0] === targetPath.split('?')[0]) {
        router.back();
        return;
    }

    router.push(targetPath);
}

export function getBreadcrumbPrefix(): null | BreadcrumbInterface {
    const snippetsStore = useSnippetsStore();
    const snippets: any = snippetsStore.snippets as unknown as any;
    if (!process.client) {
        return null;
    }

    let previousPage = getPreviousPage();
    let currentPage = getCurrentPage();

    if (!previousPage || !previousPage.url || !previousPage.type) {
        return getBreadcrumbInjectedBreadcrumbPrefix(1);
    }

    if (currentPage && currentPage.type === 'manufacturerListingPage') {
        return null;
    }

    if (isProductListingHistoryType(previousPage.type, currentPage?.type)) {
        if (currentPage && currentPage.type === 'productListingPage') {
            // show the category above the current one
            return getBreadcrumbInjectedBreadcrumbPrefix(1);
        }
        return {
            name: snippets['breadcrumbprefix.backToOverview'] ?? 'Terug naar overzicht',
            url: previousPage.url,
        };
    }

    switch (previousPage.type) {
        case 'searchPage':
            return {
                name: snippets['breadcrumbprefix.backToSearchResults'] ?? 'Terug naar zoekresultaten',
                url: previousPage.url,
            };

        case 'manufacturerListingPage':
            return getBreadcrumbInjectedBreadcrumbPrefix(1);

        case 'productDetailPage':
            return getBreadcrumbInjectedBreadcrumbPrefix(1);
        default:
            return getBreadcrumbInjectedBreadcrumbPrefix(1);
    }
}

function getBreadcrumbInjectedBreadcrumbPrefix(offset: number = 0): null | BreadcrumbInterface {
    const path = inject('breadcrumbs') as string | BreadcrumbInterface[] | Ref<BreadcrumbInterface[]> | undefined;

    const resolvedPath = unref(path);

    if (!resolvedPath || !Array.isArray(resolvedPath)) {
        return null;
    }

    const breadcrumbs = [...resolvedPath];

    if (breadcrumbs.length == 2) {
        if (breadcrumbs[breadcrumbs.length - 1].name === 'Merken') {
            return null;
        }
    }

    if (breadcrumbs.length > 2 && offset > 0) {
        const snippetsStore = useSnippetsStore();
        const snippets: any = snippetsStore.snippets as unknown as any;

        if (breadcrumbs[breadcrumbs.length - 1].name === 'Shop') {
            return null;
        }
        if (breadcrumbs[breadcrumbs.length - (offset + 1)].name === 'Shop') {
            return null;
        }

        if (breadcrumbs[breadcrumbs.length - (offset + 1)].name === 'Merken') {
            // rename for textual consistency
            breadcrumbs[breadcrumbs.length - (offset + 1)].name = 'merken';
        }

        return {
            name:
                (snippets['breadcrumbprefix.more'] ?? 'Meer') +
                ' ' +
                breadcrumbs[breadcrumbs.length - (offset + 1)].name,
            url: breadcrumbs[breadcrumbs.length - (offset + 1)].url,
        };
    }

    if (breadcrumbs.length > offset + 1) {
        const snippetsStore = useSnippetsStore();
        const snippets: any = snippetsStore.snippets as unknown as any;

        if (breadcrumbs[breadcrumbs.length - (offset + 1)].name === 'Shop') {
            return null;
        }

        return {
            name:
                (snippets['breadcrumbprefix.more'] ?? 'Meer') +
                ' ' +
                breadcrumbs[breadcrumbs.length - (offset + 1)].name,
            url: breadcrumbs[breadcrumbs.length - (offset + 1)].url,
        };
    }

    if (breadcrumbs.length > 2) {
        const snippetsStore = useSnippetsStore();
        const snippets: any = snippetsStore.snippets as unknown as any;

        if (breadcrumbs[breadcrumbs.length - 1].name === 'Shop') {
            return null;
        }

        return {
            name: (snippets['breadcrumbprefix.more'] ?? 'Meer') + ' ' + breadcrumbs[breadcrumbs.length - 1].name,
            url: breadcrumbs[breadcrumbs.length - 1].url,
        };
    }

    return null;
}
